import React, { useCallback, useMemo, useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import objectPath from 'object-path'
import { makeStyles } from '@material-ui/core/styles'
import Paper from '@material-ui/core/Paper'
import Typography from '@material-ui/core/Typography'
import Button from '@material-ui/core/Button'
import Divider from '@material-ui/core/Divider'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import FormControl from '@material-ui/core/FormControl'
import Checkbox from '@material-ui/core/Checkbox'
import TextField from '@material-ui/core/TextField'

import GlobalLoading from 'components/system/GlobalLoading'
import Loading from 'components/system/Loading'
import dateUtils from 'shared/src/date'
import DateNavigator from 'components/datepicker/DateNavigator'
import AdminCalendarKey from 'components/admin/CalendarKey'
import projectSelectors from 'store/selectors/project'
import bookingActions from 'store/modules/booking'
import bookingSelectors from 'store/selectors/booking'
import routerSelectors from 'store/selectors/router'
import routerActions from 'store/modules/router'
import Template from 'shared/src/template'
import constants from 'shared/src/constants'
import utils from 'shared/src/utils'
import useCalendar from 'components/hooks/useAdminCalendar'

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'top',
  },
  dateToolbar: {
    textAlign: 'center',
  },
  optionsToolbar: {
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    margin: theme.spacing(1),
  },
  optionsDaysField: {
    width: '80px',
    marginRight: theme.spacing(2),
  },
  optionsDaysReload: {
    marginRight: theme.spacing(2),
  },
  datePicker: {
    marginTop: '0px',
  },
  calendar: {

  },
  scheduleRoot: {
    paddingBottom: theme.spacing(4),
  },
  booking: {
    paddingLeft: theme.spacing(2),
  },
  summary: {
    width: '100%',
    padding: theme.spacing(0.5),
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  slotKey: {
    textAlign: 'left',
    justifyContent: 'flex-start',
    color: '#999'
  },
  slot: {
    padding: theme.spacing(0.5),
  },
  slotTitleContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  slotTitle: {
    width: '400px',
  },
  summaryStats: {
    flexGrow: 1,
  },
  summaryOptions: {
    flexGrow: 0,
  },
  summaryAttendance: {
    marginRight: theme.spacing(2),
  },
  divider: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  }
}))

const BookingFormSchedule = ({

}) => {
  const dispatch = useDispatch()
  const classes = useStyles()

  const [ showDetails, setShowDetails ] = useState(true)
  const [ showAllSlots, setShowAllSlots ] = useState(false)
  const [ rawDays, setRawDays ] = useState(1)
  const [ calendarDays, setCalendarDays ] = useState(1)

  const {
    id,
    date,
  } = useSelector(routerSelectors.params)
  const project = useSelector(projectSelectors.currentProjectId)
  const config = useSelector(bookingSelectors.config)

  const condensedSchedule = useMemo(() => {
    if(!config || !config.admin || !config.admin.condensed_schedule) return false
    return true
  }, [
    config,
  ])

  const calendar = useCalendar({
    project,
    bookingform: id,
    config,
    days: calendarDays,
    // we cannot change the value of condensed for schedule
    useConfigCondensed: true,
  })

  const reloadDays = useCallback(() => {
    let ret = parseInt(rawDays)
    if(isNaN(ret)) return
    if(ret > 100) ret = 100
    setRawDays(ret)
    setCalendarDays(ret)
  }, [
    rawDays,
  ])

  const schedule = useMemo(() => {

    return calendar.rawData.map(day => {
      const stats = {
        bookings: 0,
        attendance: {}
      }

      const slots = day.blocks
        .reduce((all, block) => {
          return all.concat(block.slots)
        }, [])
        // only include confirmed bookings not reserved ones
        // inject the "status" field which controls the key display and will be exported to CSV
        .map(slot => {
          const hasBookings = slot.bookingRecords.filter(booking => booking.status == constants.BOOKING_STATUS_CONFIRMED).length > 0
          const hasReservedBookings = slot.bookingRecords.filter(booking => booking.status == constants.BOOKING_STATUS_RESERVED).length > 0
          return Object.assign({}, slot, {
            bookingRecords: slot.bookingRecords.filter(booking => booking.status == constants.BOOKING_STATUS_CONFIRMED),
            hasBookings,
            hasReservedBookings,
          })
        })
        // only include slots with bookings
        .filter(slot => {
          if(showAllSlots) return true
          return slot.bookingRecords.length > 0
        })
        // process each slot with booking template
        .map(slot => {

          const useBookings = slot.bookingRecords

          useBookings.sort((a, b) => {
            const aName = a.data.details.name.toLowerCase()
            const bName = b.data.details.name.toLowerCase()
            if(aName < bName) return -1
            if(aName > bName) return 1
            return 0
          })

          const bookings = useBookings.map(booking => {
            stats.bookings++

            const bookingTemplate = Template({
              slot,
              config: calendar.config,
              options: booking.data.options,
              details: booking.data.details,
              reference: booking.reference,
              createdAt: booking.created_at,
            })

            const scheduleTemplateText = slot.templates.schedule || objectPath.get(calendar.config, 'booking_config.templates.summary')
            const templateText = scheduleTemplateText ? bookingTemplate(scheduleTemplateText) : 'no schedule template found'

            const scheduleSummaryTemplateText = slot.templates.scheduleSummary

            if(scheduleSummaryTemplateText) {
              const scheduleSummary = bookingTemplate(scheduleSummaryTemplateText)
              Object.keys(scheduleSummary).forEach(key => {
                if(!stats.attendance[key]) stats.attendance[key] = 0
                stats.attendance[key] += scheduleSummary[key]
              })
            }

            return templateText + ' (booked ' + new Date(booking.created_at).toLocaleDateString() + ')'
          })

          // reduce the useBookings into a number of adults
          const adults = useBookings.reduce((all, booking) => {
            if(booking.data && booking.data.options) {
              return all + (booking.data.options.adults || 0)
            }
            else {
              return all
            }
          }, 0)

          const children = useBookings.reduce((all, booking) => {
            if(booking.data && booking.data.options) {
              return all + (booking.data.options.children || 0)
            }
            else {
              return all
            }
          }, 0)

          const slotTemplate = Template({
            slot,
            config: calendar.config,
          })

          let annotations = []

          const canBook = slotTemplate(slot.templates.canBook, 'slot.templates.canBook')
        
          if(Object.keys(slot._overriddenValues).length > 0) annotations = annotations.concat(['hasOverride'])
          if(utils.isSlotAdminOnly(slot)) annotations = annotations.concat(['adminOnlySlot'])
          if(utils.isSlotBlocked(slot)) annotations = annotations.concat(['blockedSlot'])
          if(!canBook) {
            annotations = annotations.concat(['bookedSlot'])
          }
          else if(slot.hasBookings) {
            annotations = annotations.concat(['partiallyBookedSlot'])
          }
          else if(slot.hasReservedBookings) {
            annotations = annotations.concat(['reservedSlot'])
          }

          return {
            title: slotTemplate.render('title'),
            bookings,
            annotations,
            adults,
            children,
          }
        })

      return {
        stats,
        day,
        slots,
      }

    })
    
  }, [
    calendar.config,
    calendar.rawData,
    showAllSlots,
  ])

  const downloadCSV = useCallback(() => {
    let columns = ['date', 'slot', 'status']
    if(showDetails) columns = columns.concat(['booking'])
    const csvContent = [columns]
      .concat(
        schedule.reduce((all, scheduleDay) => {
          return all.concat(
            scheduleDay.slots.reduce((all, slot) => {
              if(showDetails) {
                return all.concat(slot.bookings.map((booking, j) => {
                  return [scheduleDay.day.date, slot.title, slot.annotations.join(' '), booking]
                }))
              }
              else {
                return all.concat([
                  [scheduleDay.day.date, slot.title, slot.annotations.join(' ')]
                ])
              }
            }, [])
          )
        }, []) 
      )
      .map((row) => {
        return row.map((cell) => {
          let ret = cell === null ? '' : cell.toString()
          ret = ret.replace(/"/g, '""')
          if (ret.search(/("|,|\n)/g) >= 0) ret = '"' + ret + '"'
          return ret
        }).join(',')
      }).join("\r\n")

    // The download function takes a CSV string, the filename and mimeType as parameters
    // Scroll/look down at the bottom of this snippet to see how download is called
    const download = (content, fileName, mimeType) => {
      const a = document.createElement('a')
      mimeType = mimeType || 'application/octet-stream'

      if (navigator.msSaveBlob) { // IE10
        navigator.msSaveBlob(new Blob([content], {
          type: mimeType
        }), fileName)
      } else if (URL && 'download' in a) { //html5 A[download]
        a.href = URL.createObjectURL(new Blob([content], {
          type: mimeType
        }))
        a.setAttribute('download', fileName)
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
      } else {
        location.href = 'data:application/octet-stream,' + encodeURIComponent(content) // only this mime type is supported
      }
    }

    download(csvContent, `${date}.csv`, 'text/csv;encoding:utf-8')

  }, [
    schedule,
    showDetails,
  ])

  useEffect(() => {
    dispatch(bookingActions.loadCalendarConfig({
      project,
      bookingform: id,
    }))
  }, [
    project,
    id,
  ])

  return (
    <div className={ classes.root }>
      <div className={ classes.dateToolbar }>
        {
          !calendar.loading && calendar.parsedDate && (
            <DateNavigator
              className={ classes.datePicker }
              value={ calendar.parsedDate }
              min={ calendar.calendarConfig.min }
              max={ calendar.calendarConfig.max }
              condensed={ calendar.condensed }
              condensedDates={ calendar.condensedDates }
              navigatorButtons={ calendar.navigatorButtons }
              onMove={ calendar.move }
              onChange={ calendar.setDate }
            />
          )
        }
      </div>
      <div className={ classes.optionsToolbar }>
        <TextField
          size="small"
          label="Days"
          type="number"
          value={ rawDays }
          className={ classes.optionsDaysField }
          onChange={ (e) => setRawDays(e.target.value) }
        />
        <Button
          size="small"
          variant="outlined"
          className={ classes.optionsDaysReload }
          onClick={ reloadDays }
        >
          reload
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={ downloadCSV }
        >
          Download CSV
        </Button>
      </div>
      <div className={ classes.optionsToolbar }>
        <FormControlLabel
          control={
            <Checkbox
              checked={ showDetails }
              onChange={ (e) => setShowDetails(e.target.checked) }
            />
          }
          label="Show Booking Details?"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={ showAllSlots }
              onChange={ (e) => setShowAllSlots(e.target.checked) }
            />
          }
          label="Show All Slots?"
        />
      </div>
      <AdminCalendarKey />
      <Divider className={ classes.divider } />
      <div className={ classes.calendar }>
        {
          schedule.map((scheduleDay, i) => {
            return (
              <div className={ classes.scheduleRoot } key={ i }>
                <div className={ classes.summary }>
                  <div className={ classes.summaryStats }>
                    <Typography variant="h6" gutterBottom>{ dateUtils.title(scheduleDay.day.date) }: { scheduleDay.day.name } : { scheduleDay.stats.bookings } bookings</Typography>
                    <Typography variant="caption" gutterBottom>
                      <Typography className={ classes.summaryAttendance } variant="caption" gutterBottom>schedule: <strong>{ scheduleDay.day.schedule }</strong></Typography>
                      {
                        Object.keys(scheduleDay.stats.attendance).map(key => (
                          <Typography className={ classes.summaryAttendance } key={ key } variant="caption" gutterBottom>{ key }: <strong>{ scheduleDay.stats.attendance[key] }</strong></Typography>
                        ))
                      }
                    </Typography>
                  </div>
                  <div className={ classes.summaryOptions }>
                    
                  </div>
                </div>
                {
                  scheduleDay.slots.map((slot, i) => {
                    return (
                      <div className={ classes.slot } key={ i }>
                        <div className={ classes.slotTitleContainer }>
                          <Typography variant={ condensedSchedule ? "caption" : "subtitle1"} className={ classes.slotTitle }>
                            { slot.title }
                          </Typography>
                          <Typography variant={ condensedSchedule ? "caption" : "subtitle1"} className={ classes.slotTitle }>
                            { slot.bookings.length } bookings, { slot.children } children, { slot.adults } adults
                          </Typography>
                          <AdminCalendarKey
                            className={ classes.slotKey }
                            names={ slot.annotations }
                          />
                        </div>
                        {
                          showDetails && (
                            condensedSchedule ?
                              slot.bookings[0] :
                              slot.bookings.map((booking, j) => (
                                <div className={ classes.booking } key={ j }>
                                  <Typography variant="caption">{ booking }</Typography>
                                </div>
                              ))
                          ) 
                        }
                      </div>
                    )
                  })
                }
                <Divider className={ classes.divider } />
              </div>
            )
          })
        }
      </div>
      {
        calendar.loading && (
          <GlobalLoading />
        )
      }
    </div>
  )
}

export default BookingFormSchedule